import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Printer, Eye, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import OwnerLayout from '../../components/OwnerLayout';
import api from '../../utils/api';

export default function OwnerQRCode() {
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const qrRef = useRef();

  useEffect(() => {
    api.get('/api/owner/restaurant')
      .then(res => setRestaurant(res.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const menuUrl = restaurant ? `${window.location.origin}/menu/${restaurant.id}` : '';

  const downloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 400; canvas.height = 400;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 0, 0, 400, 400);
      const link = document.createElement('a');
      link.download = `${restaurant.name}-qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('QR Code downloaded!');
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgStr)));
  };

  const printQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>QR Code - ${restaurant?.name}</title>
      <style>
        body { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:100vh; margin:0; font-family:Poppins,sans-serif; }
        h2 { margin-bottom:8px; color:#2C3E50; }
        p { color:#7F8C8D; font-size:14px; margin-bottom:24px; }
        svg { width:280px; height:280px; }
      </style></head>
      <body>
        <h2>${restaurant?.name}</h2>
        <p>Scan to view menu & order</p>
        ${svg.outerHTML}
        <p style="margin-top:16px;font-size:12px">${menuUrl}</p>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  return (
    <OwnerLayout title="My QR Code">
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        <div className="fade-in">
          <div className="card" style={styles.qrCard}>
            <h2 style={styles.restaurantName}>{restaurant?.name}</h2>
            <p style={styles.subtitle}>Scan to view menu & order</p>
            <div ref={qrRef} style={styles.qrWrapper}>
              <QRCodeSVG
                value={menuUrl}
                size={220}
                bgColor="#ffffff"
                fgColor="#2C3E50"
                level="H"
                includeMargin={true}
              />
            </div>
            <p style={styles.scanHint}>📱 Place this QR on your tables</p>
          </div>

          {/* Menu URL */}
          <div className="card" style={styles.urlCard}>
            <p style={styles.urlLabel}>Menu Link</p>
            <p style={styles.urlText}>{menuUrl}</p>
          </div>

          {/* Actions */}
          <div style={styles.actionsCol}>
            <button className="btn btn-primary btn-full" onClick={downloadQR}>
              <Download size={18} /> Download QR Code (PNG)
            </button>
            <button className="btn btn-secondary btn-full" onClick={printQR}>
              <Printer size={18} /> Print QR Code
            </button>
            <button className="btn btn-secondary btn-full" onClick={() => window.open(menuUrl, '_blank')}>
              <Eye size={18} /> Preview Customer Menu
            </button>
            <button className="btn btn-secondary btn-full" onClick={() => { navigator.clipboard.writeText(menuUrl); toast.success('Link copied!'); }}>
              <Copy size={18} /> Copy Menu Link
            </button>
          </div>

          {/* Tips */}
          <div className="card" style={styles.tipsCard}>
            <h3 style={styles.tipsTitle}>💡 Tips</h3>
            <ul style={styles.tipsList}>
              <li>Print QR on thick paper or laminate it</li>
              <li>Place one QR per table</li>
              <li>Test by scanning with your phone first</li>
              <li>Orders will arrive on your WhatsApp: {restaurant?.whatsapp}</li>
            </ul>
          </div>
        </div>
      )}
    </OwnerLayout>
  );
}

const styles = {
  qrCard: { textAlign: 'center', marginBottom: 12, padding: '32px 24px' },
  restaurantName: { fontSize: '20px', fontWeight: '700', color: '#2C3E50', marginBottom: 4 },
  subtitle: { fontSize: '13px', color: '#7F8C8D', marginBottom: 24 },
  qrWrapper: {
    display: 'inline-block', padding: 16,
    background: 'white', borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
    marginBottom: 16,
  },
  scanHint: { fontSize: '13px', color: '#FF6B35', fontWeight: 500 },
  urlCard: { marginBottom: 16, background: '#fff0eb' },
  urlLabel: { fontSize: '11px', fontWeight: 600, color: '#FF6B35', marginBottom: 4 },
  urlText: { fontSize: '12px', color: '#7F8C8D', wordBreak: 'break-all' },
  actionsCol: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 },
  tipsCard: { background: '#f0fdf4', border: '1px solid #bbf7d0' },
  tipsTitle: { fontSize: '14px', fontWeight: '600', color: '#166534', marginBottom: 8 },
  tipsList: { paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 },
};
